import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../utils/config';
import i18n from '../i18n';
import { DEMO_TRIP_ID, buildDemoNotes } from '../utils/demoTrip';

/**
 * Gestisce le note condivise per un trip.
 * Fetcha tutte le note al mount, espone addNote e deleteNote con aggiornamento ottimistico.
 *
 * notes: { [activityKey]: [{id, content, author, createdAt}] }
 */
export function useSharedNotes(tripId, token) {
    const [notes, setNotes]     = useState({});
    const [loading, setLoading] = useState(false);
    // Viaggio demo: note fittizie, modifiche solo in memoria (nessuna API)
    const isDemo = tripId === DEMO_TRIP_ID;

    useEffect(() => {
        if (isDemo) { setNotes(buildDemoNotes(i18n.language)); return; }
        if (!tripId || !token) return;

        let cancelled = false;
        setLoading(true);

        fetch(`${API_BASE_URL}/api/notes/${tripId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.ok ? r.json() : { notes: {} })
            .then(data => { if (!cancelled) setNotes(data.notes ?? {}); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [tripId, token, isDemo]);

    const addNote = useCallback(async (activityKey, content, author) => {
        let id = `demo-${Date.now()}`;
        if (!isDemo) {
            const res = await fetch(`${API_BASE_URL}/api/notes/${tripId}`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body:    JSON.stringify({ activityKey, content, author }),
            });
            if (!res.ok) throw new Error(i18n.t('errors.saveFailed'));
            ({ id } = await res.json());
        }

        setNotes(prev => {
            const existing = prev[activityKey] ?? [];
            return {
                ...prev,
                [activityKey]: [...existing, {
                    id,
                    content,
                    author: author || i18n.t('common.anonymous'),
                    createdAt: new Date().toISOString(),
                }],
            };
        });
    }, [tripId, token, isDemo]);

    const deleteNote = useCallback(async (id, activityKey) => {
        if (!isDemo) {
            await fetch(`${API_BASE_URL}/api/notes/${id}`, {
                method:  'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
        }
        setNotes(prev => ({
            ...prev,
            [activityKey]: (prev[activityKey] ?? []).filter(n => n.id !== id),
        }));
    }, [token, isDemo]);

    return { notes, loading, addNote, deleteNote };
}
